// Lógica compartilhada da tela de Geradoras — fonte única para detecção de
// fabricante, mapeamento de status e formatação. Os códigos de status vêm das
// documentações oficiais (contexto/documentacao):
//   - CSI Smart Energy (Qgbt*): 1 OnLine, 2 OffLine, 3 Alarm, 4 Fault,
//     5 Custom Alarm, 6 Accessing, 7 Partially offline
//   - Chint/FOMware (siteId hex de 24 chars): 2 Run, 3 Offline, 4 Abnormal
//   - Growatt (openapi v1): 1/2 operando, 0/3 falha, 4 manutenção
//   - SAJ/Elekeeper: a API não devolve código de status; quem grava é o coletor
//     n8n, derivando de isOnline + faultMsgList → 1 operando, 3 offline, 0 falha

import type { LeituraTelemetria } from '../../types';

export type Vendor = 'growatt' | 'csi' | 'chint' | 'saj';

export type PlantState = 'operando' | 'parcial' | 'offline' | 'falha' | 'manutencao' | 'desconhecido';

const VENDORS: readonly Vendor[] = ['growatt', 'csi', 'chint', 'saj'];

/**
 * O fabricante vem de `unidades_geradoras.fabricante`. O palpite pelo formato do
 * site_id_api é só fallback para cadastro antigo sem a coluna preenchida — e ele
 * NÃO distingue SAJ de Growatt (ambos numéricos), por isso a coluna existe.
 */
export const getVendor = (siteId: string, fabricante?: string | null): Vendor => {
    if (fabricante && (VENDORS as readonly string[]).includes(fabricante)) return fabricante as Vendor;
    if (siteId.startsWith('Qgbt')) return 'csi';
    if (/^[0-9a-f]{24}$/i.test(siteId)) return 'chint';
    return 'growatt';
};

export const VENDOR_LABEL: Record<Vendor, string> = {
    growatt: 'Growatt',
    csi: 'Canadian (CSI)',
    chint: 'Chint',
    saj: 'SAJ',
};

export const mapStatus = (vendor: Vendor, status: number | null | undefined): PlantState => {
    if (status === null || status === undefined) return 'desconhecido';
    if (vendor === 'csi') {
        if (status === 1) return 'operando';
        if (status === 7) return 'parcial';
        if (status === 2) return 'offline';
        if (status === 3 || status === 4 || status === 5) return 'falha';
        return 'desconhecido';
    }
    if (vendor === 'chint') {
        if (status === 2) return 'operando';
        if (status === 3) return 'offline';
        if (status === 4) return 'falha';
        return 'desconhecido';
    }
    if (vendor === 'saj') {
        if (status === 1) return 'operando';
        if (status === 3) return 'offline';
        if (status === 0) return 'falha';
        return 'desconhecido';
    }
    // Growatt
    if (status === 1 || status === 2) return 'operando';
    if (status === 0 || status === 3) return 'falha';
    if (status === 4) return 'manutencao';
    return 'desconhecido';
};

/** Estados que contam como "gerando/disponível" nos KPIs e filtros. */
export const isEstadoOperando = (state: PlantState) => state === 'operando' || state === 'parcial';

/** Rótulo do estado — o Painel e a Visão 3D precisam chamar a mesma coisa pelo mesmo nome. */
export const ESTADO_LABEL: Record<PlantState, string> = {
    operando: 'Operando',
    parcial: 'Parcial',
    offline: 'Offline',
    falha: 'Atenção',
    manutencao: 'Manutenção',
    desconhecido: 'Sem status',
};

// ---------------- Identificação amigável ----------------

const RE_BLOCO = /Bloco\s*\d+/i;
const RE_QGBT = /Qgbt\s*\d+/i;
const RE_UC = /UC:\s*(\d+)/;

/**
 * Nome/UC amigáveis por fabricante — o cadastro guarda formatos diferentes.
 * Fonte única: Painel e Análise precisam rotular a mesma usina do mesmo jeito.
 */
export const getDisplayUsina = (
    usina: { site_id_api: string; nome_usina: string; fabricante?: string | null },
): { marca: string; uc: string } => {
    const nome = usina.nome_usina;
    const vendor = getVendor(usina.site_id_api, usina.fabricante);
    if (vendor === 'csi') return { marca: 'Canadian', uc: usina.site_id_api };
    // SAJ: o site_id_api é o plantId interno da Elekeeper, ilegível para o
    // operador. O nome cadastrado ("Fazenda Morro Agudo") é o identificador útil.
    if (vendor === 'saj') return { marca: 'SAJ', uc: nome };
    if (vendor === 'chint') {
        const blocoMatch = nome.match(RE_BLOCO);
        // Qgbt1 e Qgbt2 são hardware Canadian cadastrado no portal da Chint, e
        // o nome vem "Qgbt1 - UC - 4.085.442.012-82": não casa com /UC:\s*\d+/
        // e caía no site_id_api truncado ("698e2ef6"). Aqui elas ganham o mesmo
        // rótulo das irmãs Qgbt3/4/5, que chegam pela Canadian.
        const qgbtMatch = nome.match(RE_QGBT);
        const ucMatch = nome.match(RE_UC);
        // Qgbt1/Qgbt2 aparecem como "Canadian" porque o equipamento é Canadian —
        // só o monitoramento é que passa pelo portal da Chint. Isso é rótulo:
        // getVendor continua 'chint' de propósito, senão o mapStatus leria os
        // códigos da FOMware (2=Run) na escala da CSI (2=OffLine).
        if (qgbtMatch) return { marca: 'Canadian', uc: qgbtMatch[0] };
        return {
            marca: blocoMatch ? `Chint - ${blocoMatch[0]}` : 'Chint',
            uc: ucMatch ? ucMatch[1] : usina.site_id_api.slice(0, 8),
        };
    }
    return { marca: 'Growatt', uc: usina.site_id_api };
};

/**
 * Usinas que o cadastro numera como partes de um mesmo conjunto ("Bloco 3",
 * "Qgbt4"). O agrupamento sai do NOME: o banco não tem coluna de site nem
 * coordenada. A Visão 3D desenha cada conjunto como um parque só; usina fora
 * de conjunto ganha terreno próprio.
 */
export const getConjuntoUsina = (nomeUsina: string): { id: string; titulo: string; rotulo: string } | null => {
    const bloco = nomeUsina.match(RE_BLOCO);
    if (bloco) return { id: 'blocos', titulo: 'Blocos', rotulo: bloco[0].replace(/\s+/g, ' ') };
    const qgbt = nomeUsina.match(RE_QGBT);
    if (qgbt) return { id: 'qgbt', titulo: 'Qgbt', rotulo: qgbt[0].replace(/\s+/g, '') };
    return null;
};

// "SICOOB 10022969932" → nome "SICOOB", número "10022969932". Só casa número
// longo no fim: é o formato em que o cadastro cola a UC no nome da usina.
const RE_NUMERO_FINAL = /[\s\-–:]*(\d{6,})\s*$/;

/**
 * Rótulo curto para caber em cima da usina na cena: "Bloco 1", "Qgbt3",
 * "Vinicius Cândido". O número de UC colado no fim do nome sai — a não ser que
 * duas usinas fiquem com o mesmo rótulo (as SICOOB), caso em que os 4 últimos
 * dígitos voltam para desempatar.
 */
export const getRotulosCurtos = (
    usinas: { site_id_api: string; nome_usina: string }[],
): Record<string, string> => {
    const partes = usinas.map(u => {
        const conjunto = getConjuntoUsina(u.nome_usina);
        if (conjunto) return { site: u.site_id_api, base: conjunto.rotulo, numero: '' };
        const numero = u.nome_usina.match(RE_NUMERO_FINAL)?.[1] ?? '';
        const base = u.nome_usina.replace(RE_NUMERO_FINAL, '').trim() || u.nome_usina;
        return { site: u.site_id_api, base, numero };
    });
    const vezes = new Map<string, number>();
    partes.forEach(p => vezes.set(p.base, (vezes.get(p.base) || 0) + 1));

    const rotulos: Record<string, string> = {};
    partes.forEach(p => {
        const repetido = (vezes.get(p.base) || 0) > 1;
        const sufixo = (p.numero || p.site).slice(-4);
        rotulos[p.site] = repetido ? `${p.base} …${sufixo}` : p.base;
    });
    return rotulos;
};

// ---------------- Formatação ----------------

/** W → "512 W" | "1,25 kW" | "2,35 MW" */
export const formatPower = (watts: number | null | undefined) => {
    if (watts == null) return 'N/D';
    if (watts >= 1_000_000) {
        return `${(watts / 1_000_000).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} MW`;
    }
    if (watts >= 1000) {
        return `${(watts / 1000).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kW`;
    }
    return `${watts.toLocaleString('pt-BR', { maximumFractionDigits: 0 })} W`;
};

/** kWh → "134.391 kWh" | "2,12 GWh" (antes rotulava GWh como MWh). */
export const formatKwh = (kwh: number | null | undefined) => {
    if (kwh == null) return 'N/D';
    if (kwh >= 1_000_000) {
        return `${(kwh / 1_000_000).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} GWh`;
    }
    return `${kwh.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} kWh`;
};

export const formatDateTime = (timestamp: string | undefined | null) => {
    if (!timestamp) return 'Sem dado';
    return new Date(timestamp).toLocaleString('pt-BR');
};

export const getRelativeTime = (timestamp: string | undefined | null | Date) => {
    if (!timestamp) return 'Sem dados';
    const rtf = new Intl.RelativeTimeFormat('pt-BR', { numeric: 'auto', style: 'short' });
    const elapsed = new Date(timestamp).getTime() - Date.now();
    const elapsedMins = Math.round(elapsed / 60000);
    if (Math.abs(elapsedMins) < 60) return rtf.format(elapsedMins, 'minute');
    const elapsedHours = Math.round(elapsed / 3600000);
    if (Math.abs(elapsedHours) < 24) return rtf.format(elapsedHours, 'hour');
    return rtf.format(Math.round(elapsed / 86400000), 'day');
};

export const getDurationText = (lastUpdateTime: string | null | undefined) => {
    if (!lastUpdateTime) return null;
    const diff = Date.now() - new Date(lastUpdateTime).getTime();
    const days = Math.floor(diff / 86_400_000);
    if (days === 0) {
        const hours = Math.floor(diff / 3_600_000);
        if (hours === 0) return 'Há menos de 1h';
        return `Há ${hours}h`;
    }
    return `Há ${days} dia${days > 1 ? 's' : ''}`;
};

/** Idade do dado → tom do chip (verde fresco, âmbar velho, vermelho muito velho). */
export const getFreshness = (timestamp: string | Date | null | undefined): 'fresh' | 'stale' | 'old' | 'none' => {
    if (!timestamp) return 'none';
    const ageMin = (Date.now() - new Date(timestamp).getTime()) / 60000;
    if (ageMin <= 20) return 'fresh';
    if (ageMin <= 120) return 'stale';
    return 'old';
};

// ---------------- Cálculo ----------------

export const parseNumeric = (value: unknown): number | null => {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string') {
        const parsed = Number(value.replace(',', '.'));
        return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
};

/**
 * Delta de um acumulado dentro da janela [startTimestamp, agora].
 * Retorna null quando não há baseline dentro da janela (ex.: usina com menos
 * histórico que a janela pedida) — o chamador decide o fallback.
 */
export const getDeltaByWindow = (
    series: LeituraTelemetria[],
    startTimestamp: number,
    selector: (item: LeituraTelemetria) => number | null
): number | null => {
    if (series.length === 0) return null;

    let latest: number | null = null;
    for (let i = series.length - 1; i >= 0; i--) {
        latest = selector(series[i]);
        if (latest !== null) break;
    }
    if (latest === null) return null;

    let baseline: number | null = null;
    for (let i = series.length - 1; i >= 0; i--) {
        if (new Date(series[i].data_leitura).getTime() > startTimestamp) continue;
        baseline = selector(series[i]);
        if (baseline !== null) break;
    }
    if (baseline === null) return null;

    return Math.max(latest - baseline, 0);
};

/**
 * Potência efetiva de uma usina agora: telemetria fresca (<30 min) > plataforma
 * do fabricante > telemetria velha.
 *
 * Fonte única: o Painel e a Visão 3D somam este número, e os dois totais têm
 * de bater.
 */
export const potenciaEfetivaW = (
    leitura: Pick<LeituraTelemetria, 'data_leitura' | 'potencia_atual_w'> | null | undefined,
    potenciaFabricanteW: number | null | undefined,
): number | null => {
    if (leitura && Date.now() - new Date(leitura.data_leitura).getTime() < 30 * 60 * 1000) return leitura.potencia_atual_w;
    if (potenciaFabricanteW != null) return potenciaFabricanteW;
    return leitura ? leitura.potencia_atual_w : null;
};

// prod_Xd = latest - base_Xd (telemetria); se faltar baseline, usa o valor da
// plataforma do fabricante; se nada, null (N/D).
export const deltaOr = (latest: number | null | undefined, base: number | null | undefined, gmVal: number | null | undefined): number | null => {
    if (latest != null && base != null) return Math.max(latest - base, 0);
    return gmVal ?? null;
};

/**
 * Há quanto tempo a USINA está sem reportar, a partir do carimbo da plataforma
 * do fabricante (`last_update_time`) — nunca da hora do coletor, que grava de
 * 5 em 5 minutos mesmo com a usina morta. Null dentro da folga de 3 h, que
 * cobre a janela noturna e o atraso normal.
 */
export const tempoSemReportar = (
    dadoAt: string | null | undefined,
): { horas: number; texto: string; grave: boolean } | null => {
    if (!dadoAt) return null;
    const horas = (Date.now() - Date.parse(dadoAt)) / 3600000;
    if (!Number.isFinite(horas) || horas < 3) return null;
    return {
        horas,
        texto: horas >= 48 ? `${Math.floor(horas / 24)} dias` : `${Math.floor(horas)} horas`,
        grave: horas >= 24,
    };
};

/** capacidade_instalada é armazenada em MWp (ex.: 1.100 = 1,1 MWp). */
export const capacidadeMwpToW = (capacidadeMwp: number | null | undefined): number | null => {
    if (!capacidadeMwp) return null;
    return capacidadeMwp * 1_000_000;
};

export const getFatorCapacidade = (potenciaW: number | null | undefined, capacidadeMwp: number | null | undefined): number | null => {
    const capW = capacidadeMwpToW(capacidadeMwp);
    if (potenciaW == null || !capW) return null;
    return Math.min(Math.max(Math.round((potenciaW / capW) * 100), 0), 100);
};
